import { alpha, Box, Button, Modal, Stack, Table, TableBody, TableCell, TableContainer, TableFooter, TableHead, TablePagination, TableRow, Typography } from '@mui/material';
import { green, grey, red, yellow } from '@mui/material/colors';
import 'jspdf-autotable';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { MainBorderRadius, TableHeader } from '../../../app/AppStyles';
import { DisplayName } from '../../../app/AppUtils';
import ContentLoader from '../../subcomponents/ContentLoader';
import TablePaginationActions from '../../subcomponents/TablePaginationActions';
import { fetchReportSchoolclasses, resetReportFilterValues } from '../reportFiltersSlice';
import ReportSelect from '../ReportSelect';
import ExportExcelSchoolclassOverview from './ExportExcelSchoolclassOverview';
import { fetchSchoolclassOverviewReport, resetReportProgress } from './reportProgressSlice';

import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CircleIcon from '@mui/icons-material/Circle';
import ReportIcon from '@mui/icons-material/Report';

export default function ReportSchoolclassOverviewTab() {
    const dispatch = useDispatch();

    const [searching, setSearching] = useState(false);
    const [selectedSchoolclassIds, setSelectedSchoolclassIds] = useState([]);

    const { appLanguage, resourceFinal } = useSelector((state) => state.settings.resource.resources);
    const { reportSchoolclasses } = useSelector((state) => state.reports.reportFilters);
    const { schoolclassOverviewReport, schoolclassOverviewReportStatus } = useSelector((state) => state.reports.progress);
    const { siomSettings } = useSelector((state) => state.settings.siom);
    const { appSettings } = useSelector((state) => state.settings.app);
    const { activeDepartment, defaultTheme } = useSelector((state) => state.settings.theme);

    const [showTJL, setShowTJL] = useState(false);
    const [openClassSelector, setOpenClassSelector] = useState(false);

    const classSelectorClick = () => {
        setOpenClassSelector(!openClassSelector);
    };

    const callbackModalClassSelector = () => {
        handleSearchReportClick();
        setOpenClassSelector(false);
    };

    useEffect(() => {
        if (appSettings?.appVersion === 1) {
            if (siomSettings.useMultipleDepartments) {
                if (activeDepartment?.[0].themeSettings?.tjlEnabled)
                    setShowTJL(true);
            } else {
                if (defaultTheme?.tjlEnabled)
                    setShowTJL(true);
            };
        }
    }, [appSettings, siomSettings]);

    useEffect(() => {
        dispatch(resetReportProgress())
    }, []);

    useEffect(() => {
        dispatch(resetReportFilterValues());
        dispatch(fetchReportSchoolclasses());
    }, []);

    useEffect(() => {
        if (schoolclassOverviewReportStatus === "success" || schoolclassOverviewReportStatus === "failed")
            setSearching(false);
    }, [schoolclassOverviewReportStatus]);

    const handleSearchReportClick = () => {
        const departmentId = siomSettings.useMultipleDepartments && activeDepartment ? activeDepartment[0]?.id : null;

        setSearching(true);
        dispatch(fetchSchoolclassOverviewReport({ schoolclassIds: selectedSchoolclassIds.map(item => item.schoolclassId), departmentId: departmentId }));
    };

    const TableFragment = () => {
        const [page, setPage] = useState(0);
        const [rowsPerPage, setRowsPerPage] = useState(schoolclassOverviewReport?.length < 100 ? -1 : 100);

        const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - schoolclassOverviewReport?.length) : 0;

        const handleChangePage = (event, newPage) => {
            setPage(newPage);
        };

        const handleChangeRowsPerPage = (event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
        };

        return (
            <>
                <TableBody>
                    {(rowsPerPage > 0 ? schoolclassOverviewReport?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage) : schoolclassOverviewReport)?.map((item, index) =>
                        <TableRow key={item.id} sx={{ '&:nth-of-type(odd)': { bgcolor: 'white.main' }, height: 63 }}>
                            <TableCell align="center">
                                {item.studentData.number}
                            </TableCell>
                            <TableCell align="left">{DisplayName(item.studentData.firstName, item.studentData.middleName, item.studentData.lastName)}</TableCell>
                            {!siomSettings?.alternateSchoolclassOverview &&
                                <TableCell align="center">
                                    <CircleIcon sx={{
                                        color: item.studentProgress ? (
                                            item.studentProgress?.totalProgramCompleted ? green[500] : red[500]
                                        ) : grey[500]
                                    }} />
                                </TableCell>
                            }
                            {siomSettings?.enableRequiredPrograms &&
                                <TableCell align="center">
                                    <CircleIcon sx={{
                                        color: item.studentProgress ? (
                                            item.studentProgress?.requiredPoints?.requiredPointsStatus.toLowerCase() === "green" ? green[500] :
                                                item.studentProgress?.requiredPoints?.requiredPointsStatus.toLowerCase() === "yellow" ? yellow[700] : red[500]
                                        ) : grey[500]
                                    }} />
                                </TableCell>
                            }
                            {siomSettings?.alternateSchoolclassOverview &&
                                <TableCell align="center">
                                    <CircleIcon sx={{
                                        color: item.studentProgress ? (
                                            item.studentProgress?.totalPoints.allPointsCompleted ? green[500] : red[500]
                                        ) : grey[500]
                                    }} />
                                </TableCell>
                            }
                            {showTJL &&
                                <TableCell align="center">
                                    <CircleIcon sx={{
                                        color: item.tjlStatus ? (item.tjlStatus === 2 ? green[500] :
                                            item.tjlStatus === 1 ? yellow[700] : red[500]) : grey[500]
                                    }} />
                                </TableCell>
                            }
                            {siomSettings?.alternateSchoolclassOverview && <TableCell align="center">{item.studentProgress ? item.studentProgress?.totalPoints?.minPoints : "-"}</TableCell>}
                            <TableCell align="center" sx={{ color: green[500] }}>{item.studentProgress ? item.studentProgress?.totalPoints?.achievedPoints : "-"}</TableCell>
                            {!siomSettings?.alternateSchoolclassOverview && <TableCell align="center" sx={{ color: yellow[700] }}>{item.studentProgress ? item.studentProgress?.totalPoints?.subscribedPoints : "-"}</TableCell>}
                            {!siomSettings?.alternateSchoolclassOverview && <TableCell align="center" sx={{ color: red[500] }}>{item.studentProgress ? item.studentProgress?.totalPoints?.neededPoints : "-"}</TableCell>}
                            <TableCell align="center">{item.studentProgress ? item.studentProgress?.progressFlags?.flagsYellow : "-"}</TableCell>
                            {!siomSettings?.alternateSchoolclassOverview && <TableCell align="center">{item.studentProgress ? item.studentProgress?.progressActivities?.some((item) => item.status === false) ? <ReportIcon color="error" /> : <CheckCircleIcon color="success" /> : "-"}</TableCell>}
                            <TableCell align="center">{item.studentProgress ? item.studentProgress?.progressRemarks?.remarksCount : "-"}</TableCell>
                            <TableCell align="center">{item.studentProgress ? item.studentProgress?.totalPoints?.attendancePercentage + '%' : "-"}</TableCell>
                        </TableRow>
                    )}
                    {(rowsPerPage > 0 ? schoolclassOverviewReport?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage) : schoolclassOverviewReport)?.length === 0 && <TableRow><TableCell colSpan={13}>{resourceFinal[appLanguage].messages.FILTER_RESULT_EMPTY}</TableCell></TableRow>}
                    {emptyRows > 0 && <TableRow style={{ height: 63 * emptyRows }}><TableCell colSpan={10} /></TableRow>}
                </TableBody>
                <TableFooter>
                    <TableRow>
                        <TablePagination
                            sx={{ border: 0 }}
                            rowsPerPageOptions={[10, 25, 50,
                                {
                                    label: schoolclassOverviewReport?.length < 100 ? resourceFinal[appLanguage].words.ALL : '100',
                                    value: schoolclassOverviewReport?.length < 100 ? -1 : 100
                                }
                            ]}
                            count={schoolclassOverviewReport ? schoolclassOverviewReport?.length : 0}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            ActionsComponent={TablePaginationActions}
                            labelDisplayedRows={({ from, to, count }) => { return `${from}-${to} ${resourceFinal[appLanguage].messages.OF_THE} ${count}`; }}
                            labelRowsPerPage={resourceFinal[appLanguage].messages.TABLE_ROWS_PER_PAGE}
                        />
                    </TableRow>
                </TableFooter>
            </>
        );
    };

    return (
        <>
            <Box sx={{ display: 'flex', flexDirection: 'column', flex: '1 1 auto' }}>
                <Stack direction="column">
                    <Box sx={{ display: 'flex', bgcolor: theme => alpha(theme.palette.white.main, 0.3), borderRadius: MainBorderRadius, p: 1 }}>
                        <Box sx={{ display: 'flex', mr: 1 }}>
                            <Button variant="outlined" onClick={() => classSelectorClick()}>{resourceFinal[appLanguage].words.CLASSES}</Button>
                        </Box>
                        <ExportExcelSchoolclassOverview
                            disabled={searching || !schoolclassOverviewReport}
                            jsonData={schoolclassOverviewReport}
                            fileName={`${resourceFinal[appLanguage].words.REPORT_PROGRESS} - ${selectedSchoolclassIds.map(item => item.schoolclassName)}`}
                            showRequiredProgram={siomSettings?.enableRequiredPrograms}
                            showTjl={showTJL}
                        />
                    </Box>
                    <Typography sx={{ ml: 1, fontWeight: 'bold', color: 'primary.main' }}>
                        {selectedSchoolclassIds.map((item, index) => (index ? ', ' : '') + item.schoolclassName)}
                    </Typography>
                </Stack>
                <ContentLoader
                    successCondition={searching === false}
                    errorCondition={schoolclassOverviewReport === "failed"}
                    content={
                        <TableContainer>
                            <Table size="small" id="#report">
                                <TableHead>
                                    <TableRow>
                                        <TableCell align="center" sx={{ ...TableHeader }}>{resourceFinal[appLanguage].words.STUDENT}{resourceFinal[appLanguage].words.NUMBER.toLowerCase()}</TableCell>
                                        <TableCell align="left" sx={{ ...TableHeader, flexGrow: 1 }}>{resourceFinal[appLanguage].words.NAME}</TableCell>
                                        {!siomSettings?.alternateSchoolclassOverview && <TableCell align="center" sx={{ ...TableHeader }}>{resourceFinal[appLanguage].words.PROGRAM}</TableCell>}
                                        {siomSettings?.enableRequiredPrograms && <TableCell align="center" sx={{ ...TableHeader }}>{resourceFinal[appLanguage].words.REQUIRED_PROGRAM}</TableCell>}
                                        {siomSettings?.alternateSchoolclassOverview && <TableCell align="center" sx={{ ...TableHeader }}>{resourceFinal[appLanguage].words.POINTS_COMPLETED}</TableCell>}
                                        {showTJL && <TableCell align="center" sx={{ ...TableHeader }}>{resourceFinal[appLanguage].words.FLAGS_TJL}</TableCell>}
                                        {siomSettings?.alternateSchoolclassOverview && <TableCell align="center" sx={{ ...TableHeader }}>{resourceFinal[appLanguage].words.POINTS_TO_ACQUIRE}</TableCell>}
                                        <TableCell align="center" sx={{ ...TableHeader }}>{resourceFinal[appLanguage].words.POINTS_ACHIEVED.toLowerCase()}</TableCell>
                                        {!siomSettings?.alternateSchoolclassOverview && <TableCell align="center" sx={{ ...TableHeader }}>{resourceFinal[appLanguage].words.POINTS_SUBSCRIBED.toLowerCase()}</TableCell>}
                                        {!siomSettings?.alternateSchoolclassOverview && <TableCell align="center" sx={{ ...TableHeader }}>{resourceFinal[appLanguage].words.POINTS_NEEDED.toLowerCase()}</TableCell>}
                                        <TableCell align="center" sx={{ ...TableHeader }}>{resourceFinal[appLanguage].words.FLAGS_YELLOW_MULT}</TableCell>
                                        {!siomSettings?.alternateSchoolclassOverview && <TableCell align="center" sx={{ ...TableHeader }}>{resourceFinal[appLanguage].words.ATTENDANCE}</TableCell>}
                                        <TableCell align="center" sx={{ ...TableHeader }}>{resourceFinal[appLanguage].words.REMARKS}</TableCell>
                                        <TableCell align="center" sx={{ ...TableHeader }}>{resourceFinal[appLanguage].words.ATTENDANCE_PERCENTAGE}</TableCell>
                                        <TableCell align="center" padding="checkbox"></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableFragment />
                            </Table>
                        </TableContainer>
                    }
                />
            </Box>
            <Modal open={openClassSelector} onClose={callbackModalClassSelector}>
                <>
                    <ReportSelect handleClose={callbackModalClassSelector} content={reportSchoolclasses} contentId="schoolclassId" contentDisplay="schoolclassName" contentTitle={resourceFinal[appLanguage].words.CLASSES} selectedContent={selectedSchoolclassIds} setSelectedContent={setSelectedSchoolclassIds} searchButton />
                </>
            </Modal>
        </>
    );
}
